import {
  Box,
  Button,
  Card,
  Dialog,
  DialogContent,
  DialogTitle,
  makeStyles,
  Typography,
  TextField,
  DialogActions,
  Tooltip,
  IconButton,
  Chip
} from '@material-ui/core'
import {
  EditOutlined as EditIcon,
  RestoreOutlined as ResetIcon,
  SendOutlined as SendIcon,
  SubjectOutlined as SubjectIcon,
  MailOutlineOutlined as MailIcon
} from '@material-ui/icons'
import React, { useState, useRef, useEffect } from 'react'
import TemplateForm from '../views/admin/EmailTemplates/Templates/TemplateForm'
import RichTextRenderer from 'components/RichTextRenderer'
import ConfirmationModal from 'components/ConfirmationModal'
import { cloudFunctionsInstance } from 'config/axios'
import { toast } from 'utils/notify'
import ProgressButton from 'components/ProgressButton'
import yup from 'schemas/yup'
import { useSelector } from 'react-redux'
import { Roles } from 'constants/index'

export const TemplateCard = ({
  id,
  template,
  onSaveTemplate,
  onResetTemplate,
  isLoading = false,
  templateId
}) => {
  const classes = useStyles()
  const { user } = useSelector((state) => state.auth)
  const [isOpenModal, setIsModalOpen] = useState(false)
  const [isResetModalOpen, setIsResetModalOpen] = useState(false)
  const [isTestEmailModalOpen, setIsTestEmailModalOpen] = useState(false)
  const [testEmail, setTestEmail] = useState('')
  const [isSendingTestEmail, setIsSendingTestEmail] = useState(false)
  const [testEmailError, setTestEmailError] = useState('')

  const handleOpenModal = () => setIsModalOpen(true)
  const handleCloseModal = () => setIsModalOpen(false)
  const handleOpenResetModal = () => setIsResetModalOpen(true)
  const handleCloseResetModal = () => setIsResetModalOpen(false)

  const handleResetTemplate = async () => {
    if (onResetTemplate) {
      await onResetTemplate(templateId)
      setIsResetModalOpen(false)
    }
  }

  const handleOpenTestEmailModal = () => {
    setIsTestEmailModalOpen(true)
    setTestEmail('')
    setTestEmailError('')
  }

  const handleCloseTestEmailModal = () => {
    setIsTestEmailModalOpen(false)
    setTestEmail('')
    setTestEmailError('')
  }

  const validateTestEmail = (email) => {
    try {
      yup.string().email('Invalid email address').required('Email is required').validateSync(email)
      return ''
    } catch (err) {
      return err.message
    }
  }

  const handleSendTestEmail = async () => {
    const error = validateTestEmail(testEmail)
    if (error) {
      setTestEmailError(error)
      return
    }

    setTestEmailError('')
    setIsSendingTestEmail(true)

    try {
      await cloudFunctionsInstance.post('/sendTestEmail', {
        email: testEmail,
        subject: template?.subject || '',
        body: template?.body || '',
        conferenceId: user?.conferenceId
      })
      toast.success('Test email sent.')
      handleCloseTestEmailModal()
    } catch (err) {
      const errorMessage =
        err?.response?.data?.msg || 'Failed to send test email. Please try again later.'
      toast.error(errorMessage)
    } finally {
      setIsSendingTestEmail(false)
    }
  }

  const [isExpanded, setIsExpanded] = useState(false)
  const [isTall, setIsTall] = useState(false)
  const previewRef = useRef(null)

  useEffect(() => {
    if (previewRef.current) {
      setIsTall(previewRef.current.scrollHeight > 110)
    }
  }, [template?.body])

  const onSubmit = (values) => {
    onSaveTemplate && onSaveTemplate(values, handleCloseModal)
  }

  return (
    <>
      <Card className={classes.card} variant="outlined" id={id}>
        {/* Header */}
        <Box className={classes.header}>
          <Box className={classes.headerLeft}>
            <Box className={classes.iconWrap}>
              <MailIcon className={classes.mailIcon} />
            </Box>
            <Typography className={classes.titleText}>{template?.name}</Typography>
          </Box>
          <Box className={classes.actions}>
            <Tooltip title="Edit template" placement="top">
              <IconButton className={classes.actionBtn} size="small" onClick={handleOpenModal}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {user?.role !== Roles.SUPER_ADMIN && (
              <Tooltip title="Send test email" placement="top">
                <IconButton
                  className={classes.actionBtn}
                  size="small"
                  onClick={handleOpenTestEmailModal}>
                  <SendIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {onResetTemplate && (
              <Tooltip title="Reset to default" placement="top">
                <IconButton
                  className={`${classes.actionBtn} ${classes.resetBtn}`}
                  size="small"
                  onClick={handleOpenResetModal}>
                  <ResetIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>

        {/* Body */}
        <Box className={classes.body}>
          <Box className={classes.subjectRow}>
            <SubjectIcon className={classes.subjectIcon} />
            <Typography className={classes.subjectLabel}>Subject</Typography>
            <Chip
              label={template?.subject || 'No subject set'}
              size="small"
              className={`${classes.subjectChip} ${
                !template?.subject ? classes.subjectChipEmpty : ''
              }`}
            />
          </Box>
          <Box
            ref={previewRef}
            className={`${classes.previewBox} ${isExpanded ? classes.previewBoxExpanded : ''}`}>
            <RichTextRenderer htmlContent={template?.body || ''} expanded={isExpanded} />
            {!isExpanded && isTall && <Box className={classes.previewFade} />}
          </Box>
          {isTall && (
            <Box className={classes.viewToggle} onClick={() => setIsExpanded((v) => !v)}>
              {isExpanded ? 'View less' : 'View more'}
            </Box>
          )}
        </Box>
      </Card>

      {/* Edit modal */}
      <Dialog fullWidth maxWidth="xl" open={isOpenModal}>
        <DialogTitle>
          <Typography className={classes.dialogTitle}>{template?.name}</Typography>
        </DialogTitle>
        <DialogContent className={classes.modalContent}>
          <TemplateForm
            initialValues={{
              subject: template?.subject || '',
              body: template?.body || ''
            }}
            onCancel={handleCloseModal}
            onSubmit={onSubmit}
            isLoading={isLoading}
            templateId={templateId || template?.id}
          />
        </DialogContent>
      </Dialog>

      <ConfirmationModal
        open={isResetModalOpen}
        closeModal={handleCloseResetModal}
        dialogTitle="Reset Email Template"
        dialogText={`Are you sure you want to reset "${template?.name}" to its default template? This action cannot be undone.`}
        handleConfirm={handleResetTemplate}
        inProgress={isLoading}
      />

      {/* Send test email modal */}
      <Dialog open={isTestEmailModalOpen} maxWidth="md" fullWidth>
        <DialogTitle>
          <Typography className={classes.dialogTitle}>Send Test Email</Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Enter an email address to send a test version of &quot;{template?.name}&quot;.
          </Typography>
          <TextField
            autoFocus
            label="Recipient email address"
            type="email"
            fullWidth
            variant="outlined"
            value={testEmail}
            onChange={(e) => {
              setTestEmail(e.target.value)
              if (testEmailError) setTestEmailError('')
            }}
            error={!!testEmailError}
            helperText={testEmailError}
            disabled={isSendingTestEmail}
            style={{ marginTop: '20px' }}
          />
        </DialogContent>
        <DialogActions style={{ padding: '16px 24px', gap: 8 }}>
          <Button
            onClick={handleCloseTestEmailModal}
            disabled={isSendingTestEmail}
            variant="outlined">
            Cancel
          </Button>
          <ProgressButton
            onClick={handleSendTestEmail}
            label="Send Test Email"
            inProgress={isSendingTestEmail}
            disabled={isSendingTestEmail || !testEmail.trim()}
            variant="contained"
            color="primary"
          />
        </DialogActions>
      </Dialog>
    </>
  )
}

export default TemplateCard

const useStyles = makeStyles({
  card: {
    borderRadius: '12px',
    overflow: 'hidden',
    border: '1px solid #e8edf2',
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
    transition: 'box-shadow 0.2s',
    '&:hover': {
      boxShadow: '0 4px 16px rgba(0,0,0,0.10)'
    }
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 18px',
    background: 'linear-gradient(135deg, #e0f7fa 0%, #f0fbfc 100%)',
    borderBottom: '1px solid #d0eef3'
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    minWidth: 0
  },
  iconWrap: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 34,
    height: 34,
    borderRadius: '8px',
    background: 'rgba(0,188,212,0.12)',
    flexShrink: 0
  },
  mailIcon: {
    color: '#00BCD4',
    fontSize: '18px'
  },
  titleText: {
    fontWeight: 700,
    fontSize: '14px',
    color: '#1a2b3c',
    letterSpacing: '0.2px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    flexShrink: 0
  },
  actionBtn: {
    width: 32,
    height: 32,
    borderRadius: '8px',
    color: '#00BCD4',
    backgroundColor: 'rgba(0,188,212,0.08)',
    '&:hover': {
      backgroundColor: 'rgba(0,188,212,0.18)'
    }
  },
  resetBtn: {
    color: '#f57c00',
    backgroundColor: 'rgba(245,124,0,0.08)',
    '&:hover': {
      backgroundColor: 'rgba(245,124,0,0.18)'
    }
  },
  body: {
    padding: '16px 18px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    backgroundColor: '#fff'
  },
  subjectRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px'
  },
  subjectIcon: {
    fontSize: '16px',
    color: '#9e9e9e'
  },
  subjectLabel: {
    fontSize: '12px',
    fontWeight: 600,
    color: '#9e9e9e',
    textTransform: 'uppercase',
    letterSpacing: '0.6px',
    flexShrink: 0
  },
  subjectChip: {
    fontSize: '12px',
    fontWeight: 500,
    height: '24px',
    backgroundColor: '#f0f4f8',
    color: '#374151',
    borderRadius: '6px',
    maxWidth: '100%'
  },
  subjectChipEmpty: {
    color: '#bdbdbd',
    fontStyle: 'italic'
  },
  previewBox: {
    backgroundColor: '#f9fafb',
    borderRadius: '8px',
    border: '1px solid #eef0f3',
    padding: '12px 14px',
    position: 'relative',
    fontSize: '13px',
    color: '#555',
    lineHeight: 1.6
  },
  previewBoxExpanded: {},
  previewFade: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '32px',
    background: 'linear-gradient(transparent, #f9fafb)',
    pointerEvents: 'none'
  },
  viewToggle: {
    alignSelf: 'flex-start',
    fontSize: '12px',
    fontWeight: 600,
    color: '#00BCD4',
    cursor: 'pointer',
    userSelect: 'none',
    '&:hover': {
      textDecoration: 'underline'
    }
  },
  modalContent: {
    marginTop: '1rem'
  },
  dialogTitle: {
    fontWeight: 600,
    fontSize: '18px',
    color: '#1a2b3c',
    letterSpacing: '0.3px'
  }
})
